// Restaurant settings (name, logo, taxes)

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Settings as SettingsIcon, Home, LogOut, Clock } from 'lucide-react';
import { hasPaymentProcessor, getProcessorName } from '../../../services/restaurant/restaurantService';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useRestaurant } from '../../../providers/RestaurantContext';
import { RestaurantSettings } from '../../../types/restaurant';
import { signOut } from 'firebase/auth';
import { auth } from '../../../config/firebase/auth';
import { NotificationBell } from '../components/NotificationBell';
import Logo from '../../../assets/fonts/Solo-logo.png';

const DEFAULT_RESTAURANT_SETTINGS: RestaurantSettings = {
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  language: 'es',
  currency: 'DOP',
  currencySymbol: 'RD$',
  taxRate: 18,
  serviceCharge: 0,
  allowSplitBill: true,
  allowTipping: true,
  tipOptions: [10, 15, 20],
  autoCloseTableHours: 4,
  requireStaffRating: false,
};

const SettingsScreen: React.FC = () => {
  const { 
    restaurant, 
    updateRestaurantSettings, 
    updateRestaurantName,
    setRestaurantLogo,
    canManageSettings,
    userRole 
  } = useRestaurant();
  const isSuperAdmin = userRole === 'super_admin';
  const [form, setForm] = useState<RestaurantSettings & { name: string; logoUrl?: string }>(() => ({
    ...DEFAULT_RESTAURANT_SETTINGS,
    name: '',
    logoUrl: undefined
  }));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const navigate = useNavigate();

  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (e) {
      console.error('Logout failed', e);
    }
  };

  useEffect(() => {
    if (restaurant) {
      setForm({
        ...DEFAULT_RESTAURANT_SETTINGS,
        ...(restaurant.settings || {}),
        name: restaurant.name ?? '',
        logoUrl: restaurant.logoUrl ?? undefined
      });
    } else {
      setForm({ 
        ...DEFAULT_RESTAURANT_SETTINGS,
        name: '',
        logoUrl: undefined
      });
    }
  }, [restaurant]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: name === 'taxRate' ? parseFloat(value) || 0 : value
    }));
    setMessage(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const { name, logoUrl, ...settingsToUpdate } = form;
      await updateRestaurantSettings(settingsToUpdate);
      if (name !== restaurant?.name) { 
        await updateRestaurantName(name);
      }
      if (logoUrl && logoUrl !== restaurant?.logoUrl) {
        await setRestaurantLogo(logoUrl);
      } else if (!logoUrl && restaurant?.logoUrl) {
        // Optionally handle clearing the logo
      }
      setMessage('Configuración guardada correctamente');
    } catch (err) {
      setMessage('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !restaurant?.id) return;
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setMessage('Formato no válido. Usa JPG, PNG o WebP.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setMessage('El archivo es demasiado grande (máximo 2MB)');
      return;
    }
    const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const safeExt = ['jpg', 'jpeg', 'png', 'webp'].includes(ext) ? ext : 'jpg';
    const storage = getStorage();
    const storageRef = ref(storage, `logos/${restaurant.id}.${safeExt}`);
    try {
      await uploadBytes(storageRef, file, { contentType: file.type });
      const downloadUrl = await getDownloadURL(storageRef);
      setForm(prev => ({ ...prev, logoUrl: downloadUrl }));
      setMessage('Logo subido correctamente');
    } catch (error) {
      setMessage('Error al subir el logo');
      console.error('Upload error:', error);
    }
  };

  if (!canManageSettings()) {
    return <p className="text-red-500 px-4 py-6">No tienes permiso para ver esta configuración.</p>;
  }

  return (
    <div className="min-h-screen w-full flex flex-col px-6 py-6">
      {/* Header — same as other admin screens */}
      <div className="flex items-center justify-between mb-8 w-full relative">
        <Link to="/admin" className="flex items-center gap-2 group">
          <img src={Logo} alt="YAP" className="h-10 w-auto object-contain" />
          <span className="text-3xl font-bold tracking-tighter text-white group-hover:text-yap-primary transition-colors">yap</span>
        </Link>
        <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 bg-zinc-900/40 backdrop-blur-xl border border-white/5 rounded-full">
          <Clock size={16} className="text-zinc-400" />
          <div className="flex flex-col">
            <span className="text-sm font-bold text-white leading-none">
              {currentTime.toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' })}
            </span>
            <span className="text-[10px] text-zinc-500 font-medium leading-none mt-0.5">
              {currentTime.toLocaleDateString('es-DO', { weekday: 'short', day: 'numeric', month: 'short' })}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/admin" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors font-medium">
            <Home size={18} />
            <span>Volver a inicio</span>
          </Link>
          <div className="w-px h-6 bg-zinc-700" />
          <NotificationBell />
          <div className="w-px h-6 bg-zinc-700" />
          <button type="button" onClick={handleLogout} className="flex items-center gap-2 text-sm text-zinc-400 hover:text-red-500 transition-colors font-medium">
            <LogOut size={18} />
            <span>Cerrar sesión</span>
          </button>
        </div>
      </div>

      <section className="max-w-3xl mx-auto w-full">
        <h1 className="text-2xl font-semibold text-white mb-2">Configuración del Restaurante</h1>
        <p className="text-zinc-400 text-sm mb-6">Nombre, logo e impuestos (moneda: RD$).</p>
        {message && (
          <p className={`mb-4 text-sm ${message.startsWith('Error') ? 'text-red-400' : 'text-emerald-400'}`}>
            {message}
          </p>
        )}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* General */}
          <div className="rounded-xl border border-white/10 bg-zinc-900/50 backdrop-blur-sm p-5 space-y-4">
            <h2 className="text-lg font-semibold text-white border-b border-white/10 pb-2">General</h2>
            <div>
              <label htmlFor="name" className="block mb-1.5 text-sm font-medium text-zinc-300">Nombre del Restaurante</label>
              <input
                id="name"
                name="name"
                value={form.name ?? ''}
                onChange={handleChange}
                className="w-full border border-white/10 rounded-lg px-3 py-2.5 bg-zinc-900/50 text-white placeholder-zinc-500 focus:ring-2 focus:ring-yap-primary focus:border-yap-primary"
                required
              />
            </div>
            <div>
              <label className="block mb-1.5 text-sm font-medium text-zinc-300">Logo del Restaurante</label>
              {form.logoUrl && (
                <div className="mb-2 flex flex-col items-start">
                  <img src={form.logoUrl} alt="Logo actual" className="h-20 object-contain border border-white/10 rounded-lg mb-2" />
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="block w-full text-sm text-zinc-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-yap-primary/20 file:text-yap-primary hover:file:bg-yap-primary/30"
              />
            </div>
          </div>
        
        {/* Payment Processor Section — only super_admin can see/edit */}
        {isSuperAdmin && (
          <div className="rounded-lg border bg-white shadow-sm p-4 space-y-4">
            <h2 className="text-lg font-semibold mb-2">Procesador de Pagos</h2>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Procesador:</span>
                <span className="font-medium">
                  {restaurant && restaurant.processor ? getProcessorName(restaurant.processor) : 'No configurado'}
                </span>
              </div>
              
              {restaurant && restaurant.processor === 'azul' && restaurant.azulConfig && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Merchant ID:</span>
                    <span className="font-mono text-sm">
                      {restaurant.azulConfig.merchantId || 'No configurado'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Canal:</span>
                    <span className="text-sm">
                      {restaurant.azulConfig.channel || 'No configurado'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Ambiente:</span>
                    <span className={`px-2 py-1 rounded text-sm ${
                      restaurant.azulConfig.environment === 'production' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {restaurant.azulConfig.environment === 'production' 
                        ? 'Producción' 
                        : 'Pruebas'}
                    </span>
                  </div>
                </>
              )}
              
              {restaurant && restaurant.processor === 'cardnet' && restaurant.cardnetConfig && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Merchant ID:</span>
                    <span className="font-mono text-sm">
                      {restaurant.cardnetConfig.merchantId || 'No configurado'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Ambiente:</span>
                    <span className={`px-2 py-1 rounded text-sm ${
                      restaurant.cardnetConfig.environment === 'production' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {restaurant.cardnetConfig.environment === 'production' 
                        ? 'Producción' 
                        : 'Pruebas'}
                    </span>
                  </div>
                </>
              )}
              
              {restaurant && !hasPaymentProcessor(restaurant) && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
                  <p className="text-yellow-800 text-sm">
                    ⚠️ Procesador de pagos no configurado. Contacta a soporte de YAP para configurar.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Impuestos (moneda fija: DOP) */}
        <div className="rounded-xl border border-white/10 bg-zinc-900/50 backdrop-blur-sm p-5 space-y-4">
          <h2 className="text-lg font-semibold text-white border-b border-white/10 pb-2">Impuestos</h2>
          <p className="text-sm text-zinc-400">La moneda del restaurante es pesos dominicanos (RD$).</p>
          <div>
            <label htmlFor="taxRate" className="block mb-1.5 text-sm font-medium text-zinc-300">Tasa de Impuesto ITBIS (%)</label>
            <input id="taxRate" name="taxRate" type="number" value={form.taxRate} onChange={handleChange} min={0} max={100} step={0.01} className="w-full border border-white/10 rounded-lg px-3 py-2.5 bg-zinc-900/50 text-white focus:ring-2 focus:ring-yap-primary focus:border-yap-primary" required />
          </div>
        </div>
        {/* Integración POS Section — only super_admin can see and configure */}
        {isSuperAdmin && (
          <div className="rounded-lg border bg-white shadow-sm p-4 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold">Integración POS</h2>
              {restaurant?.posConfig && (
                <span className={`text-xs px-2 py-1 rounded-full ${
                  restaurant.posConfig.enabled 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {restaurant.posConfig.enabled ? 'Activo' : 'Inactivo'}
                </span>
              )}
            </div>
            
            <p className="text-sm text-gray-600">
              Configura la integración con tu sistema de punto de venta para actualizar órdenes y mesas automáticamente.
            </p>

            {/* Current POS Info */}
            {restaurant?.posConfig ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Sistema actual:</span>
                  <span className="text-sm font-semibold text-blue-700">
                    {restaurant.posConfig.provider === 'mock' && 'Mock POS (Pruebas)'}
                    {restaurant.posConfig.provider === 'inflo' && 'InFlo POS'}
                    {restaurant.posConfig.provider === 'alegra' && 'Alegra POS'}
                    {restaurant.posConfig.provider === 'toast' && 'Toast POS'}
                    {restaurant.posConfig.provider === 'square' && 'Square POS'}
                  </span>
                </div>
                
                {restaurant.posConfig.syncSettings && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Actualización automática:</span>
                    <span className="text-sm text-gray-600">
                      {restaurant.posConfig.syncSettings.autoSync 
                        ? `Cada ${restaurant.posConfig.syncSettings.syncIntervalMinutes} min` 
                        : 'Desactivada'}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  ⚠️ No hay integración POS configurada
                </p>
              </div>
            )}

            {/* Configure Button — only for super_admin */}
            <div className="flex justify-end pt-2">
              <Link
                to="/admin/pos-config"
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                <SettingsIcon className="w-4 h-4" />
                {restaurant?.posConfig ? 'Modificar Configuración' : 'Configurar POS'}
              </Link>
            </div>
          </div>
        )}
        {/* Otros */}
        <div className="rounded-xl border border-white/10 bg-zinc-900/50 backdrop-blur-sm p-5 space-y-4">
          <h2 className="text-lg font-semibold text-white border-b border-white/10 pb-2">Otros</h2>
          <div>
            <label htmlFor="timezone" className="block mb-1.5 text-sm font-medium text-zinc-300">Zona Horaria</label>
            <input id="timezone" name="timezone" value={form.timezone} onChange={handleChange} className="w-full border border-white/10 rounded-lg px-3 py-2.5 bg-zinc-900/50 text-white focus:ring-2 focus:ring-yap-primary focus:border-yap-primary" required />
            <p className="text-xs text-zinc-500 mt-1">Zona del sistema: {Intl.DateTimeFormat().resolvedOptions().timeZone}</p>
          </div>
          <div>
            <label htmlFor="language" className="block mb-1.5 text-sm font-medium text-zinc-300">Idioma</label>
            <input id="language" type="text" name="language" value="es" readOnly className="w-full border border-white/10 rounded-lg px-3 py-2.5 bg-zinc-800/50 text-zinc-500 cursor-not-allowed" />
          </div>
          <div className="flex justify-end pt-2">
            <button type="submit" className="bg-yap-primary text-black px-6 py-2.5 rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed" disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </div>
      </form>
    </section>
    </div>
  );
};

export default SettingsScreen;
